import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { useToast, Toast } from '../../components/ui/Toast';
import { friendlyAuthError } from '../../lib/authErrors';
import { trackMetaEvent } from '../../lib/metaPixel';
import { formatPhoneBR } from '../../lib/utils';
import { normalizeBrPhone } from '../../lib/whatsappOutreach';

export default function Signup() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Abrir a página é só visualização. Lead e CompleteRegistration só
  // disparam depois do cadastro bem-sucedido (handleSubmit).
  useEffect(() => {
    trackMetaEvent('ViewContent', { content_name: 'signup_page' });
  }, []);

  const set = (k) => (e) => {
    const value = k === 'phone' ? formatPhoneBR(e.target.value) : e.target.value;
    setForm(f => ({ ...f, [k]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Preencha todos os campos.');
      return;
    }
    // Celular obrigatório: é por ele que o suporte chama (WhatsApp) quem
    // trava no onboarding ou não cria os presentes. 13 = 55 + DDD + 9 dígitos.
    if (normalizeBrPhone(form.phone).length !== 13) {
      toast.error('Informe seu celular com DDD, ex: (11) 91234-5678.');
      return;
    }
    if (form.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      await signup(form.email, form.password, form.name, form.phone);
      trackMetaEvent('Lead');
      trackMetaEvent('CompleteRegistration');
      navigate('/onboarding');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: 'name', label: 'Nome completo', type: 'text', placeholder: 'Seu nome', Icon: User, autoComplete: 'name' },
    { key: 'email', label: 'Email', type: 'email', placeholder: 'seu@email.com', Icon: Mail, autoComplete: 'email' },
    { key: 'phone', label: 'Celular', type: 'tel', placeholder: '(11) 91234-5678', Icon: Phone, autoComplete: 'tel-national', hint: 'Só pra te ajudar a deixar tudo pronto, sem spam.' },
    { key: 'password', label: 'Senha', type: showPass ? 'text' : 'password', placeholder: '••••••••', Icon: Lock, autoComplete: 'new-password' },
  ];

  const perks = [
    'Página personalizada com URL exclusiva',
    'Imagens dos presentes geradas com IA',
    'Receba os valores direto no seu Pix',
    'Grátis para começar, sem cartão',
  ];

  return (
    <div className="min-h-screen flex">
      {/* ── Left decorative panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80"
          alt="Wedding"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-rose-950/75" />

        {/* Floating decoration */}
        <div className="absolute top-16 right-12 w-40 h-40 bg-pink-300/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-6 w-28 h-28 bg-rose-300/20 rounded-full blur-2xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
            <span className="font-bold text-xl">Mimo do Casal</span>
          </Link>

          {/* Main pitch */}
          <div>
            <h2 className="text-4xl font-serif font-semibold leading-tight mb-8 text-white/95">
              Crie uma experiência de presentes inesquecível para o seu grande dia.
            </h2>

            <div className="flex flex-col gap-4 mb-8">
              {perks.map(f => (
                <div key={f} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-white/20 border border-white/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={13} className="text-white" />
                  </div>
                  <span className="text-white/85 text-sm">{f}</span>
                </div>
              ))}
            </div>

            <ol className="space-y-3">
              {[
                'Crie a conta grátis, sem cartão',
                'Monte a lista e compartilhe o link',
                'Comissão de 2,99% por presente, sem mensalidade',
              ].map((step, i) => (
                <li key={step} className="flex items-center gap-3 bg-white/10 backdrop-blur border border-white/20 rounded-xl px-4 py-3">
                  <span className="w-7 h-7 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-white/90 text-sm">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-rose-50/60 to-white overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md py-8"
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
              <span className="font-bold text-xl text-gray-900">Mimo do Casal</span>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Crie sua conta grátis 💍</h1>
            <p className="text-gray-500">Comece a criar a lista de presentes dos seus sonhos.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(({ key, label, type, placeholder, Icon, autoComplete, hint }) => (
              <div key={key}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
                <div className="relative">
                  <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={type}
                    value={form[key]}
                    onChange={set(key)}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    className="w-full pl-10 pr-12 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all hover:border-gray-300"
                  />
                  {key === 'password' && (
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                </div>
                {hint && <p className="text-xs text-gray-400 mt-1.5">{hint}</p>}
              </div>
            ))}

            {/* Password strength indicator */}
            {form.password.length > 0 && (
              <div className="flex gap-1.5 items-center">
                {[1, 2, 3, 4].map(n => (
                  <div
                    key={n}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      form.password.length >= n * 2
                        ? form.password.length < 6
                          ? 'bg-amber-400'
                          : 'bg-emerald-400'
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
                <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">
                  {form.password.length < 4 ? 'Fraca' : form.password.length < 6 ? 'Razoável' : 'Forte'}
                </span>
              </div>
            )}

            <Button type="submit" size="lg" className="w-full mt-2" loading={loading}>
              {!loading && (
                <>
                  <span>Criar minha conta</span>
                  <ArrowRight size={18} />
                </>
              )}
              {loading && <span>Criando conta...</span>}
            </Button>

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              Ao criar a conta, você aceita os{' '}
              <Link to="/termos" target="_blank" className="text-rose-500 hover:text-rose-600 underline">Termos de Uso</Link>{' '}
              e a{' '}
              <Link to="/privacidade" target="_blank" className="text-rose-500 hover:text-rose-600 underline">Política de Privacidade</Link>.
            </p>
          </form>

          {/* Sign in link */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Já tem conta?{' '}
            <Link to="/login" className="text-rose-500 font-semibold hover:text-rose-600 transition-colors">
              Entrar
            </Link>
          </p>
        </motion.div>
      </div>

      <Toast toasts={toast.toasts} removeToast={toast.removeToast} />
    </div>
  );
}
